<script setup lang="ts">
useHead({
  title: 'Terms of Service – SpreadIt',
  meta: [
    { name: 'description', content: 'SpreadIt Terms of Service — the rules that govern your use of the SpreadIt platform.' },
  ],
});
const lastUpdated = 'April 20, 2025';
</script>

<template>
  <div class="max-w-3xl mx-auto px-6 py-14 text-gray-800 dark:text-gray-200">
    <!-- Header -->
    <div class="mb-10">
      <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-2">Terms of Service</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400">Last updated: {{ lastUpdated }}</p>
    </div>

    <div class="prose prose-gray dark:prose-invert max-w-none space-y-8">

      <p>
        Please read these Terms of Service ("Terms") carefully before using SpreadIt (the "Service") operated by SpreadIt
        ("us", "we", or "our"). By accessing or using the Service you agree to be bound by these Terms. If you disagree with
        any part, you may not use the Service.
      </p>

      <!-- 1 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">1. Eligibility</h2>
        <p class="text-sm leading-relaxed">
          You must be at least 13 years of age (or the minimum age of digital consent in your country) to use the Service.
          By accepting these Terms you represent that you meet this requirement and have the legal capacity to enter into a
          binding agreement.
        </p>
      </section>

      <!-- 2 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">2. Accounts</h2>
        <p class="text-sm leading-relaxed">
          You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur
          under your account. Notify us immediately at
          <a href="mailto:support@spreadit.app" class="text-primary-600 dark:text-primary-400 underline">support@spreadit.app</a>
          if you suspect unauthorized access. We reserve the right to terminate accounts that violate these Terms.
        </p>
        <p class="text-sm leading-relaxed mt-2">
          When you sign in using a third-party identity provider (e.g., Google), you grant us the right to retrieve basic profile
          information (name, email, profile picture) from that provider for the purpose of creating and maintaining your account.
          We will not request permissions beyond what is necessary for account creation and authentication.
        </p>
      </section>

      <!-- 3 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">3. Acceptable Use</h2>
        <p class="text-sm leading-relaxed">You agree not to use the Service to:</p>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed mt-2">
          <li>Violate any applicable local, national, or international law or regulation.</li>
          <li>Transmit unsolicited or unauthorized advertising, spam, or promotional material.</li>
          <li>Distribute malware, viruses, or any other harmful software.</li>
          <li>Attempt to gain unauthorized access to any part of the Service or its related systems.</li>
          <li>Scrape, crawl, or systematically extract data from the Service without prior written consent.</li>
          <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity.</li>
          <li>Harass, abuse, or harm another person.</li>
          <li>Interfere with or disrupt the integrity or performance of the Service.</li>
          <li>Circumvent any rate-limiting or access-control mechanisms.</li>
        </ul>
        <p class="text-sm leading-relaxed mt-2">
          We reserve the right to suspend or terminate your account at our sole discretion if we determine you have violated
          these restrictions.
        </p>
      </section>

      <!-- 4 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">4. Subscriptions and Billing</h2>
        <p class="text-sm leading-relaxed">
          Certain features of the Service require a paid subscription. By selecting a paid plan you authorize us (via our
          payment processor, Stripe) to charge the applicable fees to your payment method on a recurring basis.
        </p>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed mt-2">
          <li>Subscriptions renew automatically at the end of each billing cycle unless cancelled beforehand.</li>
          <li>You may cancel your subscription at any time from your account settings; cancellation takes effect at the end of the current period.</li>
          <li>All fees are non-refundable except as required by law or as otherwise stated in our refund policy.</li>
          <li>We may change subscription pricing with at least 30 days' advance notice.</li>
        </ul>
      </section>

      <!-- 5 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">5. Your Content</h2>
        <p class="text-sm leading-relaxed">
          You retain ownership of any scripts, templates, data, and other content you create or upload ("User Content"). By
          using the Service you grant us a limited, worldwide, royalty-free licence to host, store, and process your User
          Content solely to operate and improve the Service. We will not use your User Content for any other purpose.
        </p>
        <p class="text-sm leading-relaxed mt-2">
          You are solely responsible for ensuring your User Content does not violate any third-party rights, applicable laws,
          or these Terms.
        </p>
      </section>

      <!-- 6 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">6. Intellectual Property</h2>
        <p class="text-sm leading-relaxed">
          The Service and all associated software, design, text, graphics, logos, and other materials are owned by or licensed
          to us and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify,
          distribute, sell, or lease any part of the Service or its content without our express prior written permission.
        </p>
      </section>

      <!-- 7 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">7. Third-Party Services</h2>
        <p class="text-sm leading-relaxed">
          The Service may integrate with or link to third-party services (e.g., Google Sheets, external APIs you configure).
          We are not responsible for the availability, accuracy, or practices of those services. Your use of third-party
          services is governed by their respective terms and privacy policies.
        </p>
      </section>

      <!-- 8 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">8. Disclaimer of Warranties</h2>
        <p class="text-sm leading-relaxed">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
          BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT
          WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.
        </p>
      </section>

      <!-- 9 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">9. Limitation of Liability</h2>
        <p class="text-sm leading-relaxed">
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SPREADIT OR ITS OFFICERS, DIRECTORS,
          EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
          INCLUDING LOSS OF DATA, LOSS OF PROFITS, OR BUSINESS INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH YOUR USE
          OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p class="text-sm leading-relaxed mt-2">
          OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE
          GREATER OF (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) USD $50.
        </p>
      </section>

      <!-- 10 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">10. Indemnification</h2>
        <p class="text-sm leading-relaxed">
          You agree to defend, indemnify, and hold harmless SpreadIt and its affiliates, officers, directors, employees, and
          agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees)
          arising from or related to your use of the Service, your User Content, or your violation of these Terms.
        </p>
      </section>

      <!-- 11 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">11. Termination</h2>
        <p class="text-sm leading-relaxed">
          We may suspend or terminate your access to the Service at any time, with or without notice, for conduct that we
          believe violates these Terms or is harmful to other users, us, or third parties. You may terminate your account at
          any time by visiting the account settings. Upon termination, your right to use the Service ceases immediately.
        </p>
      </section>

      <!-- 12 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">12. Governing Law and Dispute Resolution</h2>
        <p class="text-sm leading-relaxed">
          These Terms are governed by the laws of the State of Georgia, United States, without regard to its conflict of law
          provisions. Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in
          accordance with the rules of the American Arbitration Association, except that either party may seek injunctive
          relief in a court of competent jurisdiction for intellectual property infringement or other urgent matters.
        </p>
      </section>

      <!-- 13 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">13. Changes to These Terms</h2>
        <p class="text-sm leading-relaxed">
          We reserve the right to modify these Terms at any time. We will provide at least 14 days' notice of material changes
          via email or a prominent notice on the Service. Your continued use of the Service after the effective date of the
          revised Terms constitutes your acceptance of the changes.
        </p>
      </section>

      <!-- 14 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">14. Contact Us</h2>
        <p class="text-sm leading-relaxed">
          If you have any questions about these Terms, please contact us at:
        </p>
        <address class="not-italic mt-3 text-sm leading-relaxed">
          <strong>SpreadIt</strong><br />
          Email: <a href="mailto:legal@spreadit.app" class="text-primary-600 dark:text-primary-400 underline">legal@spreadit.app</a>
        </address>
      </section>
    </div>

    <!-- Footer nav -->
    <div class="mt-12 pt-6 border-t border-gray-200 dark:border-gray-800 flex gap-4 text-sm text-gray-500">
      <NuxtLink to="/privacy" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Privacy Policy</NuxtLink>
      <NuxtLink to="/" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Back to Home</NuxtLink>
    </div>
  </div>
</template>
